import React, { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

type AdminSidebarProps = {
  collapsed: boolean;
  onToggle: () => void;
};

type SidebarItem = {
  label: string;
  to?: string;
  icon: React.ReactNode;
  children?: SidebarItem[];
};

// Torre de control: cabina vidriada sobre el fuste, en trazo para que tome
// el color del ítem (activo, hover, tema).
const ControlTowerIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: "block", margin: "0 auto" }}>
    <path d="M12 1.5v2.5" />
    <rect x="5.5" y="4" width="13" height="2.2" rx="0.6" fill="currentColor" stroke="none" />
    <path d="M6.5 6.2h11l-1.6 5.3H8.1z" />
    <path d="M10.4 6.6v4.5M13.6 6.6v4.5" strokeWidth="1.3" />
    <path d="M10 11.5 9.2 20.5M14 11.5l.8 9" />
    <path d="M5 21h14" />
  </svg>
);

const sidebarItems: SidebarItem[] = [
  {
    // Todo el circuito operativo (día a día, recorridos y depósito) vive acá
    // adentro. Las URLs de cada pantalla no cambian, esto sólo las agrupa.
    label: "Operación",
    icon: "◐",
    children: [
      {
        label: "Operación del día",
        to: "/admin/operacion",
        icon: "○",
      },
      {
        // Colectas y repartos no son envíos: son recorridos, y van aparte.
        label: "Recorridos",
        icon: "↝",
        children: [
          {
            label: "Del día",
            to: "/admin/recorridos",
            icon: "≣",
          },
          {
            label: "Colectas fijas",
            to: "/admin/recorridos/fijas",
            icon: "↻",
          },
        ],
      },
      {
        // Módulo Colectas (ver informes "LYNX — Colectas: diseño ajustado"
        // y "LYNX — Colectas WebApp: interfaz unificada y confirmaciones
        // diarias"): sucesor de "Colectas fijas", con terminología de
        // dominio ("Colecta", no "Ronda") y horario por tienda. "Recorridos"
        // arriba no se toca -- convive hasta que se decida reemplazarlo.
        label: "Colectas",
        icon: "⛁",
        children: [
          {
            label: "Colectas",
            to: "/admin/colectas",
            icon: "☰",
          },
          {
            label: "En vivo",
            to: "/admin/colectas/en-vivo",
            icon: "◉",
          },
        ],
      },
      {
        label: "Depósito",
        icon: "▦",
        children: [
          {
            label: "Planta",
            to: "/admin/deposito/planta",
            icon: "▣",
          },
          {
            label: "Estanterías",
            to: "/admin/deposito/estanterias",
            icon: "▦",
          },
          {
            // Lo que cada chofer tiene que devolver: plata y paquetes.
            label: "Baúl",
            to: "/admin/deposito/baul",
            icon: "◧",
          },
        ],
      },
      {
        label: "Choferes en vivo",
        to: "/admin/choferes/mapa",
        icon: "◉",
      },
      {
        label: "Torre de control",
        to: "/admin/torre-control",
        icon: ControlTowerIcon,
      },
      {
        // Turbo automático: los despachos del día y las reglas (radio,
        // franjas) que Mercado Libre puede cambiar.
        label: "Turbo",
        icon: "◆",
        children: [
          {
            label: "Hoy en vivo",
            to: "/admin/turbo/hoy",
            icon: "◉",
          },
          {
            label: "Despachos",
            to: "/admin/turbo/despachos",
            icon: "≣",
          },
          {
            label: "Configuración",
            to: "/admin/turbo/config",
            icon: "◈",
          },
        ],
      },
    ],
  },
  {
    label: "Dashboard",
    to: "/admin",
    icon: "◫",
  },
  {
    label: "Performance",
    to: "/admin/performance",
    icon: "◪",
  },
  {
    label: "Envíos",
    icon: "⬚",
    children: [
      {
        label: "Lista de envíos",
        to: "/admin/envios",
        icon: "≣",
      },
      {
        label: "Lista legacy",
        to: "/admin/envios/legacy",
        icon: "▥",
      },
      {
  label: "Cotizar LynxPack",
  to: "/admin/envios/lynxpack",
  icon: "◈",
},
      {
        label: "Carga de envíos",
        to: "/admin/envios/carga",
        icon: "▣",
      },
      {
        label: "Impresión de etiquetas",
        to: "/admin/envios/etiquetas",
        icon: "▤",
      },
    ],
  },
  {
    // Toda la sección (incluidos los ítems legacy de abajo) sólo se muestra
    // si el usuario tiene finance_access (ver LIQUIDACIONES_ITEM más abajo).
    // Carga manual / Totales son deuda del pipeline legacy viejo, quedan
    // como están hasta que se jubilen — Clientes es la pantalla nueva.
    label: "Liquidaciones",
    icon: "◭",
    children: [
      {
        label: "Dashboard",
        to: "/admin/liquidaciones/dashboard",
        icon: "◫",
      },
      {
        // Visible: "Stores" (ver auditoría final UX) — el dominio actual es
        // Store, "Clientes" era terminología legacy. La ruta/el componente
        // no se renombran, solo el texto que ve el usuario.
        label: "Stores",
        to: "/admin/liquidaciones/clientes",
        icon: "▤",
      },
      {
        label: "Choferes",
        to: "/admin/liquidaciones/choferes",
        icon: "▥",
      },
      {
        label: "Carga manual",
        to: "/admin/liquidaciones/carga-manual",
        icon: "▣",
      },
      {
        label: "Totales",
        to: "/admin/liquidaciones/totales",
        icon: "◩",
      },
    ],
  },
  {
    // Visible para Coordinador/Liquidaciones/Admin Total (por perfil, ver
    // visibleItems más abajo) — un Coordinador la ve SIN ver "Liquidaciones".
    label: "Reportes Operativos",
    to: "/admin/reportes-operativos",
    icon: "🗒",
  },
  {
    // Usuarios y Tiendas se movieron acá adentro (antes eran ítems de nivel
    // superior) -- mismas rutas, mismos permisos, solo cambia dónde viven en
    // la navegación. Cada hijo sigue pidiendo su propia capacidad (ver
    // visibleItems más abajo): Usuarios con user_access, el resto con
    // db_access -- "Sistema" ya no es un bloque monolítico de un solo
    // permiso.
    label: "Sistema",
    icon: "▤",
    children: [
      {
        label: "Usuarios",
        to: "/admin/users",
        icon: "◌",
      },
      {
        label: "Tiendas",
        to: "/admin/stores",
        icon: "◎",
      },
      {
        label: "Warehouses",
        to: "/admin/sistema/warehouses",
        icon: "▣",
      },
      {
        label: "Listas de precios",
        to: "/admin/sistema/listas-de-precios",
        icon: "▥",
      },
      {
        label: "Zonas",
        to: "/admin/sistema/zonas",
        icon: "◇",
      },
      {
        // Monitoreo del adaptador TRANSITORIO LightData -> Current: se borra
        // junto con server/integrations/lightdata.
        label: "LightData",
        to: "/admin/sistema/lightdata",
        icon: "⇄",
      },
      {
        // Lo que mandan los usuarios desde el botón 💬 de la barra.
        label: "Sugerencias y errores",
        to: "/admin/sistema/sugerencias",
        icon: "✎",
      },
    ],
  },
];

const sidebarStyle = (collapsed: boolean): React.CSSProperties => ({
  width: collapsed ? "92px" : "272px",
  minWidth: collapsed ? "92px" : "272px",
  height: "100vh",
  position: "sticky",
  top: 0,
  borderRight: "1px solid var(--border)",
  background: "var(--surface)",
  backdropFilter: "var(--glass)",
  WebkitBackdropFilter: "var(--glass)",
  boxShadow: "var(--shadow)",
  display: "flex",
  flexDirection: "column",
  transition: "width var(--transition-base), min-width var(--transition-base)",
  zIndex: 30,
  overflow: "hidden",
});

const topSectionStyle: React.CSSProperties = {
  padding: "18px 16px 10px 16px",
  flexShrink: 0,
};

const brandRowStyle = (collapsed: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: collapsed ? "center" : "flex-start",
  gap: "14px",
  minHeight: "52px",
});

// El logo es también el botón que despliega y contrae el menú, igual que en
// DriverSidebar: al pasar el mouse, el ícono de Lynx se desvanece y aparece
// la flecha. El ícono va como máscara para tomar el color del tema.
const logoBoxStyle: React.CSSProperties = {
  width: "44px",
  height: "44px",
  borderRadius: "16px",
  position: "relative",
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--surface-alt)",
  border: "1px solid var(--border)",
  color: "var(--text-primary)",
  flexShrink: 0,
  overflow: "hidden",
  cursor: "pointer",
};

const logoImageStyle: React.CSSProperties = {
  width: "28px",
  height: "28px",
  display: "block",
  backgroundColor: "var(--text-primary)",
  WebkitMaskImage: "url('/adaptive-icon.png')",
  maskImage: "url('/adaptive-icon.png')",
  WebkitMaskSize: "contain",
  maskSize: "contain",
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskPosition: "center",
  maskPosition: "center",
};

const logoLayerStyle = (visible: boolean): React.CSSProperties => ({
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  opacity: visible ? 1 : 0,
  transform: visible ? "scale(1)" : "scale(0.82)",
  transition: "opacity 160ms ease, transform 160ms ease",
  pointerEvents: "none",
});

const toggleArrowStyle: React.CSSProperties = {
  fontSize: "25px",
  fontWeight: 700,
  lineHeight: 1,
  color: "var(--text-primary)",
};

const brandMetaStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
};

const brandEyebrowStyle: React.CSSProperties = {
  fontSize: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "var(--text-secondary)",
};

const brandNameStyle: React.CSSProperties = {
  fontSize: "17px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const navScrollStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  paddingBottom: "10px",
};

const navStyle: React.CSSProperties = {
  padding: "8px 10px 0 10px",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
};

const bottomSectionStyle: React.CSSProperties = {
  padding: "12px 10px 16px 10px",
  flexShrink: 0,
};

const footerCardStyle = (collapsed: boolean): React.CSSProperties => ({
  border: "1px solid var(--border)",
  borderRadius: "16px",
  background: "rgba(255,255,255,0.03)",
  padding: collapsed ? "10px" : "12px",
  textAlign: collapsed ? "center" : "left",
});

const getItemBaseStyle = ({
  active,
  collapsed,
}: {
  active: boolean;
  collapsed: boolean;
}): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: collapsed ? "center" : "space-between",
  gap: "10px",
  minHeight: "46px",
  padding: collapsed ? "0" : "0 14px",
  borderRadius: "18px",
  textDecoration: "none",
  color: active ? "var(--text-primary)" : "var(--text-secondary)",
  background: active ? "rgba(198,165,106,0.10)" : "transparent",
  border: active ? "1px solid var(--border)" : "1px solid transparent",
  transition: "all var(--transition-base)",
  fontWeight: active ? 700 : 600,
  fontSize: "14px",
});

const iconWrapStyle: React.CSSProperties = {
  width: "20px",
  textAlign: "center",
  fontSize: "15px",
  flexShrink: 0,
};

const labelStyle: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const leftRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  minWidth: 0,
};

const childListStyle = (depth: number): React.CSSProperties => ({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  paddingTop: "5px",
  paddingLeft: `${10 + (depth - 1) * 8}px`,
});

const getChildStyle = (active: boolean, depth: number): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: "9px",
  minHeight: "36px",
  borderRadius: "14px",
  padding: `0 12px 0 ${12 + (depth - 1) * 10}px`,
  textDecoration: "none",
  color: active ? "var(--text-primary)" : "var(--text-secondary)",
  background: active ? "rgba(198,165,106,0.08)" : "transparent",
  border: active ? "1px solid var(--border)" : "1px solid transparent",
  transition: "all var(--transition-base)",
  fontSize: "13px",
  fontWeight: active ? 700 : 500,
});

// Un grupo está "activo" si la ruta actual coincide con alguno de sus hijos,
// sin importar cuántos niveles de subgrupo haya en el medio.
const isGroupActive = (item: SidebarItem, currentPath: string): boolean => {
  if (!item.children?.length) return false;
  return item.children.some((child) =>
    child.to ? currentPath.startsWith(child.to) : isGroupActive(child, currentPath)
  );
};

type SidebarNodeProps = {
  item: SidebarItem;
  depth: number;
  collapsed: boolean;
  currentPath: string;
  openGroups: Record<string, boolean>;
  toggleGroup: (key: string) => void;
};

// Un ítem sin hijos es un link; un ítem con hijos es un grupo colapsable que
// se dibuja a sí mismo recursivamente, así "Operación" puede contener tanto
// hojas (Operación del día) como subgrupos (Recorridos, Depósito).
const SidebarNode = ({
  item,
  depth,
  collapsed,
  currentPath,
  openGroups,
  toggleGroup,
}: SidebarNodeProps) => {
  const hasChildren = Boolean(item.children?.length);
  const groupKey = item.to ?? item.label;

  if (!hasChildren) {
    return (
      <NavLink
        to={item.to!}
        end={item.to === "/admin"}
        style={({ isActive }) =>
          depth === 0
            ? getItemBaseStyle({ active: isActive, collapsed })
            : getChildStyle(isActive, depth)
        }
        title={collapsed ? item.label : undefined}
      >
        <div style={leftRowStyle}>
          <span style={iconWrapStyle}>{item.icon}</span>
          {!collapsed && <span style={labelStyle}>{item.label}</span>}
        </div>
      </NavLink>
    );
  }

  const isOpen = openGroups[groupKey];
  const groupActive = isGroupActive(item, currentPath);

  return (
    <div>
      <button
        type="button"
        onClick={() => !collapsed && toggleGroup(groupKey)}
        style={{
          ...(depth === 0
            ? getItemBaseStyle({ active: groupActive, collapsed })
            : getChildStyle(groupActive, depth)),
          width: "100%",
          cursor: "pointer",
        }}
        title={collapsed ? item.label : undefined}
      >
        <div style={leftRowStyle}>
          <span style={iconWrapStyle}>{item.icon}</span>
          {!collapsed && <span style={labelStyle}>{item.label}</span>}
        </div>

        {!collapsed && (
          <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
            {isOpen ? "▾" : "▸"}
          </span>
        )}
      </button>

      {!collapsed && isOpen && (
        <div style={childListStyle(depth + 1)}>
          {item.children!.map((child) => (
            <SidebarNode
              key={child.to ?? child.label}
              item={child}
              depth={depth + 1}
              collapsed={collapsed}
              currentPath={currentPath}
              openGroups={openGroups}
              toggleGroup={toggleGroup}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const AdminSidebar = ({ collapsed, onToggle }: AdminSidebarProps) => {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [brandHovered, setBrandHovered] = useState(false);
  const { financeAccess, adminProfile, capabilities } = useAuth();
  const canSeeReports = adminProfile === "COORDINADOR" || adminProfile === "LIQUIDACIONES" || adminProfile === "ADMIN_TOTAL";

  // Ver informe "LYNX — Fase 1.2A" y auditoría de cierre "LYNX Fase 1"
  // (Bloqueador 1): el Sidebar tiene que ocultar exactamente lo mismo que
  // el backend ya rechaza, ni más ni menos. La autoridad real sigue siendo
  // requireAdminPermit/requireProfile en cada endpoint — esto es sólo para
  // no mostrar una puerta que el server va a cerrar con 403.
  //
  // "Sistema" dejó de ser un bloque de un solo permiso cuando Usuarios y
  // Tiendas se mudaron adentro (navegación, sin tocar rutas/permisos):
  // ahora cada hijo pide su propia capacidad, igual que ya hacía
  // "Depósito" dentro de "Operación". El grupo en sí se muestra si queda
  // AL MENOS un hijo visible -- nunca una carpeta vacía.
  const visibleItems = useMemo(
    () =>
      sidebarItems
        .filter((item) => {
          if (item.label === "Liquidaciones") return financeAccess;
          if (item.label === "Reportes Operativos") return canSeeReports;
          if (item.label === "Sistema") return capabilities?.db_access === true || capabilities?.user_access === true;
          return true;
        })
        .map((item) => {
          // "Depósito" cuelga de "Operación" — se oculta solo ese hijo sin
          // esconder el resto del grupo (Recorridos), que no pide ninguna
          // capacidad puntual.
          // "Torre de control" pide db_access en el backend
          // (server/legacy/controlTower/controlTower.routes.js), igual que
          // todo "Turbo" (server/current/turbo/turbo.js), "Operación del día"
          // (server/current/ops/day.js) y "Choferes en vivo", el GPS de todos
          // los choferes (server/current/driver/locations.js).
          if (item.label === "Operación" && item.children) {
            return {
              ...item,
              children: item.children.filter((child) => {
                if (child.label === "Depósito") return capabilities?.warehouse_access === true;
                if (child.label === "Torre de control") return capabilities?.db_access === true;
                if (child.label === "Turbo") return capabilities?.db_access === true;
                if (child.label === "Operación del día" || child.label === "Choferes en vivo") return capabilities?.db_access === true;
                return true;
              }),
            };
          }
          if (item.label === "Sistema" && item.children) {
            return {
              ...item,
              children: item.children.filter((child) =>
                child.label === "Usuarios" ? capabilities?.user_access === true : capabilities?.db_access === true
              ),
            };
          }
          return item;
        }),
    [financeAccess, canSeeReports, capabilities]
  );
  const currentPath =
    typeof window !== "undefined" ? window.location.pathname : "";

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <aside style={sidebarStyle(collapsed)}>
      <div style={topSectionStyle}>
        <div style={brandRowStyle(collapsed)}>
          <button
            type="button"
            style={logoBoxStyle}
            onClick={onToggle}
            onMouseEnter={() => setBrandHovered(true)}
            onMouseLeave={() => setBrandHovered(false)}
            onFocus={() => setBrandHovered(true)}
            onBlur={() => setBrandHovered(false)}
            title={collapsed ? "Expandir menú" : "Contraer menú"}
            aria-label={collapsed ? "Expandir menú" : "Contraer menú"}
          >
            <span style={logoLayerStyle(!brandHovered)} aria-hidden="true">
              <span style={logoImageStyle} />
            </span>
            <span style={logoLayerStyle(brandHovered)} aria-hidden="true">
              <span style={toggleArrowStyle}>{collapsed ? "›" : "‹"}</span>
            </span>
          </button>

          {!collapsed && (
            <div style={brandMetaStyle}>
              <span style={brandEyebrowStyle}>Plataforma logística</span>
              <span style={brandNameStyle}>Lynx</span>
            </div>
          )}
        </div>
      </div>

      <div style={navScrollStyle}>
        <nav style={navStyle}>
          {visibleItems.map((item) => (
            <SidebarNode
              key={item.to ?? item.label}
              item={item}
              depth={0}
              collapsed={collapsed}
              currentPath={currentPath}
              openGroups={openGroups}
              toggleGroup={toggleGroup}
            />
          ))}
        </nav>
      </div>

      <div style={bottomSectionStyle}>
        <div style={footerCardStyle(collapsed)}>
          {!collapsed ? (
            <>
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--text-secondary)",
                  marginBottom: "6px",
                }}
              >
                Entorno
              </div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                }}
              >
                Panel administrativo
              </div>
            </>
          ) : (
            <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
              ●
            </span>
          )}
        </div>
      </div>
    </aside>
  );
};

export default AdminSidebar;